import http from './http';

import { createLogger } from '../utils/logger';

const logger = createLogger('bodyArrangements');
export interface TraditionalBurialDetails {
  openCasket?: boolean;
  burialClothes?: string;
  wearGlasses?: boolean;
  favoriteHatKnickknack?: string;
  favoriteShoes?: string;
}

export interface CremationDetails {
  bodyPreparationMethod?: string;
  buryInCasketAfterCremation?: boolean;
  sendAshesToSpace?: boolean;
}

export interface ScienceDonationDetails {
  medicalSchoolPreference?: string;
  forensicResearch?: boolean;
  forProfitCompanies?: boolean;
  bodyWorldExhibit?: boolean;
}

export interface GreenBurialDetails {
  noEmbalmingFluids?: boolean;
  biodegradableCasket?: boolean;
  naturalGrave?: boolean;
  mausoleum?: boolean;
}

export interface MilitaryBurialDetails {
  traditionalMilitaryHonors?: boolean;
  flagDrapedCasket?: boolean;
  rifleVolleySalute?: boolean;
  playingOfTaps?: boolean;
  flagPresentation?: boolean;
}

export interface OtherArrangementDetails {
  restAtSea?: boolean;
  liquidify?: boolean;
  crashTestSimulations?: boolean;
  returnToSoil?: boolean;
  becomeATree?: boolean;
  customDescription?: string;
}

export type ArrangementDetails =
  | TraditionalBurialDetails
  | CremationDetails
  | ScienceDonationDetails
  | GreenBurialDetails
  | MilitaryBurialDetails
  | OtherArrangementDetails
  | null;

export interface BodyArrangementsInfoRec {
  id: string;
  userId: string;
  arrangementType?: string;
  ashesDisposition?: string; // What to do with ashes (for cremation)
  specificInstructions?: string;
  preferredLocation?: string;
  contactName?: string;
  contactPhone?: string;
  estimatedCost?: number;
  notes?: string;
  arrangementDetails?: ArrangementDetails;
  username?: string;
  password?: string;
  accountNumber?: string;
  isLocked?: boolean;
}

class BodyArrangementsInfoService {
  async getAllBodyArrangementsInfo(): Promise<BodyArrangementsInfoRec[]> {
    const response = await http.get('/api/body-arrangements');

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to fetch body arrangements information');
    }

    return response.data.data || [];
  }

  async getBodyArrangementsInfoById(id: string): Promise<BodyArrangementsInfoRec | null> {
    try {
      const response = await http.get(`/api/body-arrangements/${id}`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch body arrangements information');
      }

      return response.data.data || null;
    } catch (error) {
      logger.error('Error fetching body arrangements information by ID:', error);
      return null;
    }
  }

  async createBodyArrangementsInfo(bodyArrangementsData: Partial<BodyArrangementsInfoRec>): Promise<BodyArrangementsInfoRec> {
    const response = await http.post('/api/body-arrangements', bodyArrangementsData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save body arrangements information');
    }

    return response.data.data;
  }

  async updateBodyArrangementsInfo(id: string, bodyArrangementsData: Partial<BodyArrangementsInfoRec>): Promise<BodyArrangementsInfoRec> {
    const response = await http.patch(`/api/body-arrangements/${id}`, bodyArrangementsData);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to update body arrangements information');
    }

    return response.data.data;
  }

  async deleteBodyArrangementsInfo(id: string): Promise<void> {
    const response = await http.delete(`/api/body-arrangements/${id}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete body arrangements information');
    }
  }

  async lockBodyArrangementsInfo(id: string): Promise<void> {
    logger.debug('[Service Debug] Calling lock API for body arrangements ID:', id);
    const response = await http.post(`/api/body-arrangements/${id}/lock`);
    logger.debug('[Service Debug] Lock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to lock body arrangements information');
    }
  }

  async unlockBodyArrangementsInfo(id: string, granteeEmails: string[]): Promise<void> {
    logger.debug('[Service Debug] Calling unlock API for body arrangements ID:', id, 'granteeEmails:', granteeEmails);
    const response = await http.post(`/api/body-arrangements/${id}/unlock`, { granteeEmails });
    logger.debug('[Service Debug] Unlock API response:', response.data);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to unlock body arrangements information');
    }
  }

  async getBodyArrangementsAccessGrants(id: string): Promise<any[]> {
    try {
      const response = await http.get(`/api/body-arrangements/${id}/access-grants`);

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to fetch body arrangements access grants');
      }

      return response.data.data || [];
    } catch (error) {
      logger.error('Error fetching body arrangements access grants:', error);
      return [];
    }
  }

  async deleteBodyArrangementsAccessGrant(id: string, grantId: string): Promise<void> {
    const response = await http.delete(`/api/body-arrangements/${id}/access-grants/${grantId}`);

    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to delete access grant');
    }
  }
}

export const bodyArrangementsInfoService = new BodyArrangementsInfoService();